import type { Address } from '@paid-ui/schemas/zod/address';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { isNil } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { ACNInput } from '@paid-ui/components/acn-input';
import { Button } from '@paid-ui/components/button';
import {
  Asterisk,
  FormControl,
  FormField,
  FormHelper,
  FormItem,
  FormLabel,
} from '@paid-ui/components/form';
import { InlineWarning } from '@paid-ui/components/inline-warning';
import { Input } from '@paid-ui/components/input';
import { RadioGroup, RadioGroupCard } from '@paid-ui/components/radio-group';
import { Tooltip } from '@paid-ui/components/tooltip';
import { useBusinessACNCheck } from '@paid-ui/queries/business-acn-check';

import { roleInTrusteeCompanyOptions } from '../../constants/business';
import { signUpManager } from '../../models';
import { SignUpStep } from '../../models/enums';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepTrusteeCompanyDetails: React.FC<Props> = ({ disabled, goToStep }) => {
  const router = useRouter();
  const form = useFormContext();
  const { currentStep } = useSnapshot(signUpManager);
  const [acn, setACN] = useState('');
  const [acknowledgedAcn, setAcknowledgedAcn] = useState<string | null>(null);
  const { data, refetch, isFetching } = useBusinessACNCheck({ acn });
  const companyName = form.watch('companyName');
  const _acn = form.watch('acn');
  const roleInCompany = form.watch('roleInCompany');
  const isValid =
    Boolean(companyName && _acn && roleInCompany) &&
    !form.getFieldState('companyName').invalid &&
    !form.getFieldState('acn').invalid;
  const showWarning = data === true && _acn === acknowledgedAcn;

  const proceed = () => {
    goToStep?.(SignUpStep.USER_DETAILS);
  };

  const handleNext = () => {
    const currentAcn = form.getValues('acn');
    if (acknowledgedAcn === currentAcn) {
      proceed();
      return;
    }
    setACN(currentAcn);
    form.trigger('acn');
    setTimeout(refetch, 100);
  };

  const handleSignIn = () => {
    const urlSearchParams = new URLSearchParams(globalThis.location.search);
    urlSearchParams.delete('step');
    router.push({
      pathname: '/signin',
      search: urlSearchParams.toString(),
    });
  };

  useEffect(() => {
    if (isNil(data)) {
      return;
    }

    if (data) {
      setAcknowledgedAcn(acn);
    } else {
      proceed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching]);

  useEffect(() => {
    if (currentStep === SignUpStep.TRUSTEE_COMPANY_DETAILS) {
      setAcknowledgedAcn(null);
    }
  }, [currentStep]);

  if (currentStep !== SignUpStep.TRUSTEE_COMPANY_DETAILS) {
    return null;
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <FormField
          name="companyName"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder="My company name" disabled={disabled} />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
        <FormField
          name="acn"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ACNInput {...field} placeholder="My ACN" disabled={disabled} />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
      </div>
      <FormField
        name="roleInCompany"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Does your company have multiple directors? <Asterisk />
            </FormLabel>
            <FormControl>
              <RadioGroup
                {...field}
                onValueChange={(value) =>
                  form.setValue('roleInCompany', value, {
                    shouldValidate: true,
                  })
                }
                disabled={disabled}
                className="space-y-3.5"
              >
                {roleInTrusteeCompanyOptions.map((option) => (
                  <Tooltip key={option.value} title={option.tooltip}>
                    <div>
                      <RadioGroupCard value={option.value}>{option.label}</RadioGroupCard>
                    </div>
                  </Tooltip>
                ))}
              </RadioGroup>
            </FormControl>
            <FormHelper />
          </FormItem>
        )}
      />
      <InlineWarning hidden={!showWarning}>
        This ACN is already registered. You can continue signup or sign in instead.
      </InlineWarning>
      <div className="flex gap-3">
        <Button
          variant="solid"
          className="h-[44px]"
          disabled={disabled || !isValid}
          loading={isFetching}
          onClick={handleNext}
        >
          {isFetching ? 'Checking...' : 'Continue'}
        </Button>
        {showWarning ? (
          <Button variant="outline" disabled={disabled} className="h-[44px]" onClick={handleSignIn}>
            Sign in
          </Button>
        ) : null}
      </div>
    </div>
  );
};
